"use client";

import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { toast } from "@/lib/notify";
import { STOP_IMPERSONATION_TOAST_KEY } from "@/utils/impersonate";

/**
 * One-shot listener that, on first mount after a hard reload, checks
 * for the localStorage flag set by `stopBackendImpersonation` and
 * displays a toast confirming the switch back to the admin's own
 * session. The flag is cleared as soon as the toast fires so it
 * doesn't repeat on subsequent navigations.
 */
export default function StopImpersonationToast() {
  const t = useTranslations("StopImpersonation");
  useEffect(() => {
    if (typeof window === "undefined") return;
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(STOP_IMPERSONATION_TOAST_KEY);
    } catch {
      return;
    }
    if (!raw) return;
    try {
      localStorage.removeItem(STOP_IMPERSONATION_TOAST_KEY);
    } catch {
      /* ignore */
    }
    let payload: { name?: string | null; at?: number } | null = null;
    try {
      payload = JSON.parse(raw);
    } catch {
      /* ignore */
    }
    // Stale flag (older than 30s) probably means a non-active stop —
    // skip rather than confuse the user with a delayed message.
    if (payload?.at && Date.now() - payload.at > 30_000) return;

    const name = payload?.name?.trim();
    const message = name
      ? t("stoppedWithName", { name })
      : t("stopped");
    toast.success(message, { duration: 5000 });
  }, [t]);

  return null;
}
